import { Col, Row, Form, Button } from 'react-bootstrap';
import { useState, useCallback } from 'react';
import { PlasticCodes, ItemStates } from '../../enums';
import { useNavigate } from "react-router-dom";

import BasicSpinner from '../common/basic-spinner';
import UpdateFormTags from '../common/update-form-tags';
import PlasticCodeChecks from '../common/plastic-code-checks';
import { Toaster, createToast } from '../common/toasts';
import { useAuth } from "../../utils/auth-provider";
import { deleteUserItem } from '../../utils/fetch-helpers';
import RemoveButton from '../common/remove-button';

//================================================================

export default function ItemUpdateForm({item, onUpdate, onCancel}){

  const user = useAuth();

  const navigate = useNavigate();

  const [name, setName] = useState(item.name);
  const [itemDescription, setItemDescription] = useState(item.item_description);
  const [envDescription, setEnvDescription] = useState(item.env_description);
  const [plasticCodes, setPlasticCodes] = useState(item.plastic_codes);
  const [itemState, setItemState] = useState(item.item_state);
  const [isPrivate, setIsPrivate] = useState(item.is_private);
  const [remove_tags,setRemoveTags ] = useState([]);
  const [add_tags,setAddTags ] = useState([]);
  const [working, setWorking] = useState(false);
  const [toasts, setToasts] = useState([]);

  //----------------------------------------------------------------

  const updatePlasticCodes = useCallback((code) => {
    if (plasticCodes.includes(code)){
      setPlasticCodes(plasticCodes.filter(e => e !== code));
    } else {
      setPlasticCodes([...plasticCodes, code]);
    }
  }, [plasticCodes])

  const sendUpdateRequest = useCallback(async (data, itemId) => {
    if (Object.keys(data).length <= 0) return null;

    setWorking(true);
    const updatedItem = await fetch(`/api/item/${itemId}`, {
      method : 'PUT',
      body : JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${user.token}`
      }
    })
    .then(async (response) => {
      const result = await response.json();
      if(response.ok) {
        onUpdate(result);
        return result
      }
      setToasts([...toasts, createToast(
        "Failed to update item",
        `${response.status} - ${response.json()}`
      )]);
    })
    .catch((error) => {
      setToasts([...toasts, createToast(
        "Error",
        `Failed to update item ${itemId}: ${error.message}`
      )]);
    });
    setWorking(false);
    return updatedItem;
  }, [onUpdate]);

  const submitHandler = useCallback(async () => {
    const data = {};

    if (name !== item.name){
      data['name'] = name;
    }

    if (itemDescription !== item.item_description){
      data['item_description'] = itemDescription;
    }

    if (envDescription !== item.env_description){
      data['env_description'] = envDescription;
    }

    if(plasticCodes !== item.plastic_codes){
     data['plastic_codes'] = plasticCodes;
    }

    if(itemState !== item.item_state){
     data['item_state'] = itemState;
    }

    if (isPrivate !== item.is_private){
      data['is_private'] = isPrivate;
    }

    if (add_tags.length > 0) data['add_tags'] = add_tags;
    if (remove_tags.length > 0) data['remove_tags'] = remove_tags;

    if (Object.keys(data).length > 0){
      await sendUpdateRequest(data, item._id);
    }
    onCancel();
  }, [isPrivate, name, itemDescription, envDescription, plasticCodes, itemState, item, onCancel]);

  //----------------------------------------------------------------

  const handleDelete = useCallback(async (itemId) => {
    setWorking(true);
    const deleteResult = deleteUserItem(user, itemId);
    if (deleteResult){
      setWorking(false);
      navigate("/dashboard/items");
    } else {
      setWorking(false);
      setToasts([...toasts, createToast(
        `Failed to delete item`
      )]);
    }

  },[toasts]);

  //----------------------------------------------------------------

  const updateTags = (data) => {
    setAddTags(data['add_tags']);
    setRemoveTags(data['remove_tags']);
  };

  //----------------------------------------------------------------

  return(
    <Form>

      <Form.Group className="mb-4">
        <Form.Label>Name</Form.Label>
        <Form.Control
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={working}
        />
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Item Description</Form.Label>
        <Form.Control
          as="textarea"
          value={itemDescription}
          placeholder="Enter description"
          onChange={(event) => setItemDescription(event.target.value)}
          disabled={working}
        />
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Environment Description</Form.Label>
        <Form.Control
          as="textarea"
          value={envDescription}
          placeholder="Enter description"
          onChange={(event) => setEnvDescription(event.target.value)}
          disabled={working}
        />
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Plastic Codes</Form.Label>
        <PlasticCodeChecks
          selected={plasticCodes}
          onChange={updatePlasticCodes}
          disabled={working}
        />
      </Form.Group>

      <Form.Group className="mb-4">
        <Form.Label>Item State</Form.Label>
        <Form.Select
          onChange={(event) => setItemState(event.target.value)}
          disabled={working}
          defaultValue={itemState}
        >
          {Object.keys(ItemStates).map(key =>
            <option
              key={`itemState-option-${key}`}
              value={ItemStates[key]}
            >
              {ItemStates[key]}
            </option>
          )}
        </Form.Select>
      </Form.Group>

      <UpdateFormTags
        object={item}
        onChange={updateTags}
        disabled={working}
      />

      {/* PRIVATE */}
        <Form.Group className="mb-4">
          <Form.Check
            type='checkbox'
            defaultChecked={isPrivate}
            onClick={(event) => setIsPrivate(event.target.checked)}
            label='Private'
          />
        </Form.Group>

      <Row className="align-items-center mb-4">
        <Col className="text-start">
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={working}
          >
            Cancel
          </Button>
        </Col>
        <Col className="text-end">
          <Button
            className="text-nowrap"
            variant="primary"
            onClick={submitHandler}
            disabled={working}
          >
            {working ?
              <BasicSpinner text="Saving Changes..." />
              :
              "Save Changes"
            }
          </Button>
        </Col>
      </Row>

      <Row>
        <Col className="text-end">
          <span className="me-2">
            Permanently delete
          </span>
          <RemoveButton
            objectId={item._id}
            id={`row-${item._id}`}
            onClick={handleDelete}
            buttonIcon="trash"
          />
        </Col>
      </Row>

      <Toaster toasts={toasts} />

    </Form>
  );
}
